import React from "react";

import {
	ProjectBody,
	ProjectFooterNav,
	ProjectHeader,
	ProjectImage,
	UnorderedList,
} from "./ProjectComponents";
import usePageMeta from "../../hooks/usePageMeta";
import { getProject } from "../../data/projects";

import Img1 from "../../assets/images/travelbuddy/Wallpaper.png";
import Img2 from "../../assets/images/travelbuddy/user-flow.png";
import Img4 from "../../assets/images/travelbuddy/Wallpaper-1.png";
import Img5 from "../../assets/images/travelbuddy/Wallpaper-2.png";
import Img6 from "../../assets/images/travelbuddy/Wallpaper-3.png";
import Img7 from "../../assets/images/travelbuddy/Wallpaper-4.png";
import Img8 from "../../assets/images/travelbuddy/Wallpaper-5.png";
import Img9 from "../../assets/images/travelbuddy/Wallpaper-6.png";

import "./project-styles.css";

const project = getProject("travelbuddy");

export default function TravelBuddy() {
	usePageMeta(
		"TravelBuddy",
		"TravelBuddy: a carpooling app concept for Pakistan, designed around rider safety. A product design case study by Muhammad Munaf."
	);

	return (
		<main id="main-content" tabIndex={-1} className="project-page">
			<ProjectHeader
				title="TravelBuddy"
				subheading="A carpooling app concept for Pakistan, designed to make sharing a ride with a stranger feel safe and simple."
				status={project.status}
				role="Product Designer"
				year="2022"
				users="Daily commuters in Pakistan"
			/>
			<div className="project-content">
				<ProjectImage
					src={Img1}
					alt="Three TravelBuddy screens: sign in, home with ride search, and ride details with driver profile and cost per passenger"
				/>

				<ProjectBody title="The Challenge">
					<p>
						Traffic, rising fuel prices and long commutes were making
						daily travel in Pakistan expensive and tiring. Carpooling
						could help, but few people were willing to ride with a
						stranger.
					</p>
				</ProjectBody>

				<ProjectBody title="Research">
					<p>
						I started by looking at existing apps. The one local
						option had been abandoned over security issues, and the
						most popular European app, <b>BlaBlaCar</b>, didn’t
						operate in Pakistan.
					</p>
					<p>
						Conversations with university friends, online forums and
						people in my social circles kept coming back to one
						thing: security. People didn’t feel safe sharing a car
						with someone they didn’t know.
					</p>
					<p>
						That set the goal: design a carpooling experience for
						Pakistan that people could trust.
					</p>
				</ProjectBody>

				<ProjectBody title="Approach">
					<p>
						BlaBlaCar already solved many of the core flows well, so
						I studied it closely: testing its features, mapping its
						user flow and sketching wireframes. The aim wasn’t to
						reinvent carpooling, but to adapt it to local needs.
					</p>
					<p>
						Alongside a mood board, I used that understanding of the
						flow and information architecture to shape the first
						design drafts.
					</p>
					<ProjectImage
						src={Img2}
						alt="TravelBuddy user flow diagram covering sign up, verification, searching, booking and posting rides"
					/>
					<p>
						I then shared the drafts with potential users and used
						their feedback to refine the app for Pakistani
						commuters.
					</p>
				</ProjectBody>

				<ProjectBody title="Design Decisions">
					<p>
						<b>Color.</b> The original blue palette, borrowed from
						BlaBlaCar, felt too clinical to the people I spoke with.
						After trying a few alternatives, orange was the clear
						favorite.
					</p>
					<p>
						<b>Trust and safety.</b> To address safety concerns, the
						design requires identity verification (CNIC and photo)
						before anyone can book or post a ride. I also designed
						an emergency menu on the ride-tracking screen, with
						proposed options to send an SOS to trusted contacts,
						share live location, alert the police, and report a
						passenger or driver. Because TravelBuddy was a concept,
						these were never built or connected to any real
						service.
					</p>
					<ProjectImage
						src={Img4}
						alt="Ride-tracking screen and its emergency options sheet: Send SOS, Share Live Location, Inform Police Forces, and Report Passenger or Owner"
					/>
				</ProjectBody>

				<ProjectBody title="Simplifying the Home Screen">
					<p>
						The app needed to work for everyone, from tech-savvy
						students to people less comfortable with smartphones.
						Feedback showed the home screen had too many options for
						people who just wanted to post or find a ride.
					</p>
					<p>
						The fix was a simple start screen with two clear
						actions, Post a Ride and Search for a Ride, before the
						full home screen.
					</p>
					<ProjectImage
						src={Img5}
						alt="Before and after flow: users previously landed on a busy home screen; now a start screen offers Post a Ride and Search for a Ride first"
					/>
				</ProjectBody>

				<ProjectBody title="Designs">
					<p>Key screens from the final design.</p>
					<ProjectImage
						src={Img6}
						alt="Sign up, phone number entry, and mobile verification code screens"
					/>
					<ProjectImage
						src={Img7}
						alt="Identity verification screens: enter CNIC details, upload both sides of the CNIC, and upload a face photo"
					/>
					<ProjectImage
						src={Img8}
						alt="Ride search results with a ride alert confirmation and advanced filters for luggage, women-only rides and children"
					/>
					<ProjectImage
						src={Img9}
						alt="Posting a ride: select vehicle and seats, set ride preferences, and schedule a recurring ride"
					/>
				</ProjectBody>

				<ProjectBody title="What I Learned">
					<UnorderedList
						items={[
							<>
								<b>Start with the real barrier.</b> Research
								showed trust, not features, was what stopped
								people from carpooling.
							</>,
							<>
								<b>Learn from what works.</b> Building on proven
								patterns from an established app let me focus
								on local needs.
							</>,
							<>
								<b>Design for the least confident user.</b> A
								simple start screen helped people who were less
								comfortable with technology.
							</>,
							<>
								<b>Let feedback change the design.</b> Choices
								like the color palette and home screen came
								directly from what people told me.
							</>,
						]}
					/>
				</ProjectBody>

				<ProjectFooterNav slug="travelbuddy" />
			</div>
		</main>
	);
}
